import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../admin/api';
import { PATH_DASHBOARD } from '../../routes/paths';
import { AdminRouteLayout, RequestState } from './AdminRoutePageShared';
import {
  AdminActionBar,
  AdminDataTable,
  AdminDetailPanel,
  AdminNoDataState,
  AdminStatusChip,
  AdminTimeline,
} from '../../sections/admin/shared';

const LOOKUP_CONFIG = {
  blockchain: {
    title: 'Timeline by Blockchain Transaction',
    description:
      'Use the documented blockchain transaction lookup when ops starts from a blockchain transaction id.',
    fieldLabel: 'Blockchain transaction ID',
    fieldPlaceholder: 'Enter blockchain transaction id',
    path: PATH_DASHBOARD.blockchainOps.blockchainTx,
    buildEndpoint: (value) => `/transactions/crypto_timeline/blockchain/${encodeURIComponent(value)}`,
  },
  quote: {
    title: 'Timeline by Quote',
    description: 'Use the quote lookup when operations starts from a Quidax quote id.',
    fieldLabel: 'Quote ID',
    fieldPlaceholder: 'Enter quote id',
    path: PATH_DASHBOARD.blockchainOps.quote,
    buildEndpoint: (value) => `/transactions/crypto_timeline/quote/${encodeURIComponent(value)}`,
  },
  transaction: {
    title: 'Timeline by Lens Transaction',
    description: 'Use the internal transaction lookup when ops starts from the transaction explorer.',
    fieldLabel: 'Lens transaction ID',
    fieldPlaceholder: 'Enter internal transaction id',
    path: PATH_DASHBOARD.blockchainOps.transaction,
    buildEndpoint: (value) => `/transactions/crypto_timeline/transaction/${encodeURIComponent(value)}`,
  },
};

const ACTIONABLE_RECOVERY_STATUSES = new Set([
  'blocked_aml',
  'blocked_on_hold',
  'recovery_in_progress',
  'recovery_failed',
]);

const ACTIONABLE_DISPATCH_STATUSES = new Set(['pending_dispatch', 'dispatch_failed']);

function formatLabel(value) {
  if (!value) {
    return 'Unknown';
  }

  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}

function sortChronologically(items = []) {
  return [...items].sort((left, right) => {
    const leftTime = new Date(left.created_at || left.updated_at || 0).getTime();
    const rightTime = new Date(right.created_at || right.updated_at || 0).getTime();
    return leftTime - rightTime;
  });
}

function buildDetailItems(record, fields) {
  if (!record) {
    return [];
  }

  return fields
    .filter((field) => record[field] !== undefined && record[field] !== null && record[field] !== '')
    .map((field) => ({
      label: formatLabel(field),
      value: formatValue(record[field]),
    }));
}

function buildTableRows(items = []) {
  return sortChronologically(items).map((item, index) => ({
    id: item.id || item.case_id || item.reference || item.quote_id || item.resource_id || `${index}-${item.created_at || 'row'}`,
    ...item,
  }));
}

function buildEventTimeline(events = []) {
  return sortChronologically(events).map((event, index) => ({
    id: event.id || `${event.event_name || event.resource_id || 'event'}-${index}`,
    title: event.event_name || event.resource_type || 'Domain event',
    subtitle: [event.event_scope, event.resource_type, event.resource_id].filter(Boolean).join(' • '),
    time: event.created_at || event.updated_at || 'Unknown time',
    description: event.actor_user_id ? `Actor user id: ${event.actor_user_id}` : 'No actor id supplied',
    color: 'info',
  }));
}

function buildSwapRisk(swaps = []) {
  const incompleteFinalization = swaps.find(
    (swap) => String(swap.status).toLowerCase() === 'completed' && Number(swap.is_finalized) === 0
  );

  if (!incompleteFinalization) {
    return null;
  }

  return 'A swap is marked completed while finalization is still incomplete. Treat this as high-risk.';
}

function buildRecoveryOutcome(actionResponse) {
  if (!actionResponse) {
    return null;
  }

  switch (actionResponse.action) {
    case 'noop':
      return {
        severity: 'info',
        title: 'No recovery action was required',
        message: actionResponse.reason
          ? `The backend returned noop with reason ${actionResponse.reason}.`
          : 'The backend returned noop, so no additional work was queued.',
      };
    case 'case_only':
      return {
        severity: 'warning',
        title: 'Case created for review',
        message: actionResponse.case_id
          ? `A review case was created with case id ${actionResponse.case_id}.`
          : 'The backend created a review case instead of dispatching recovery work.',
      };
    case 'recover_blocked_deposit':
      return {
        severity: 'success',
        title: 'Blocked deposit recovery queued',
        message: 'Recovery work was accepted and queued for the blocked deposit.',
      };
    case 'redispatch_conversion':
      return {
        severity: 'success',
        title: 'Conversion redispatch accepted',
        message: 'The conversion handoff was safely queued to continue processing.',
      };
    case 'retry_failed_swap':
      return {
        severity: 'success',
        title: 'Failed swap retry accepted',
        message: 'The backend accepted the failed swap retry request.',
      };
    default:
      return {
        severity: 'info',
        title: 'Recovery response received',
        message: `The backend returned action ${actionResponse.action || 'unknown'}.`,
      };
  }
}

function JsonPayloadDialog({ open, title, value, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Card variant="outlined">
          <CardContent>
            <Typography
              component="pre"
              variant="body2"
              sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {JSON.stringify(value, null, 2)}
            </Typography>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

JsonPayloadDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
};

JsonPayloadDialog.defaultProps = {
  value: null,
};

function RecoveryDialog({ open, form, loading, onClose, onChange, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Confirm blockchain recovery action</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This action will call the documented blockchain recovery endpoint. The optional fields below are passed
            through exactly as entered.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={form.reason}
            placeholder="ops_release"
            onChange={(event) => onChange('reason', event.target.value)}
            fullWidth
          />
          <TextField
            label="Actor (optional)"
            value={form.actor}
            placeholder="staff_id_or_display_identifier"
            onChange={(event) => onChange('actor', event.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton color="warning" variant="contained" loading={loading} onClick={onConfirm}>
          Run recovery
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

RecoveryDialog.propTypes = {
  form: PropTypes.shape({
    actor: PropTypes.string,
    reason: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default function BlockchainOpsPage({ lookupMode }) {
  const config = LOOKUP_CONFIG[lookupMode];
  const [identifier, setIdentifier] = useState('');
  const [requestState, setRequestState] = useState({ loading: false, error: null, data: null });
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({ reason: '', actor: '' });
  const [recoveryState, setRecoveryState] = useState({ loading: false, error: null, data: null });
  const [jsonDialog, setJsonDialog] = useState({ open: false, title: '', value: null });

  useEffect(() => {
    setIdentifier('');
    setRequestState({ loading: false, error: null, data: null });
    setRecoveryDialogOpen(false);
    setRecoveryForm({ reason: '', actor: '' });
    setRecoveryState({ loading: false, error: null, data: null });
  }, [lookupMode]);

  const timelineData = requestState.data?.data || null;
  const blockchain = timelineData?.blockchain || null;
  const transaction = timelineData?.transaction || null;
  const swaps = timelineData?.swaps || [];
  const recoveryStatus = blockchain?.recovery_status || '';
  const dispatchStatus = blockchain?.conversion_dispatch_status || '';
  const swapRisk = buildSwapRisk(swaps);
  const recoveryActionable =
    Boolean(blockchain?.id) &&
    (ACTIONABLE_RECOVERY_STATUSES.has(recoveryStatus) || ACTIONABLE_DISPATCH_STATUSES.has(dispatchStatus));

  const summaryAlert = useMemo(() => {
    if (!blockchain) {
      return null;
    }

    if (ACTIONABLE_RECOVERY_STATUSES.has(recoveryStatus)) {
      return {
        severity: 'warning',
        title: 'Recovery state requires operator attention',
        message: `Recovery status is ${formatLabel(recoveryStatus)}.`,
      };
    }

    if (ACTIONABLE_DISPATCH_STATUSES.has(dispatchStatus)) {
      return {
        severity: 'warning',
        title: 'Dispatch state requires review',
        message: `Conversion dispatch status is ${formatLabel(dispatchStatus)}.`,
      };
    }

    if (swapRisk) {
      return {
        severity: 'error',
        title: 'High-risk swap finalization state detected',
        message: swapRisk,
      };
    }

    return {
      severity: 'info',
      title: 'Timeline loaded',
      message: 'The blockchain anchor and related records were loaded successfully.',
    };
  }, [blockchain, dispatchStatus, recoveryStatus, swapRisk]);

  const recoveryOutcome = useMemo(() => buildRecoveryOutcome(recoveryState.data?.data), [recoveryState.data]);

  const transactionItems = buildDetailItems(transaction, [
    'reference',
    'transaction_type',
    'status',
    'description',
    'user_id',
    'created_at',
    'updated_at',
  ]);

  const blockchainItems = buildDetailItems(blockchain, [
    'txid',
    'coin_id',
    'amount',
    'status',
    'swap_status',
    'recovery_status',
    'recovery_reason',
    'conversion_dispatch_status',
    'conversion_dispatch_error',
    'confirmations',
    'target_confirmations',
    'created_at',
    'updated_at',
  ]);

  const recoveryItems = buildDetailItems(recoveryState.data?.data, [
    'action',
    'reason',
    'blockchain_tx_id',
    'transaction_id',
    'case_id',
    'outbox_job_id',
  ]);

  const handleLookup = async () => {
    if (!identifier.trim()) {
      setRequestState({ loading: false, error: `Enter a ${config.fieldLabel.toLowerCase()} first.`, data: null });
      return;
    }

    setRequestState({ loading: true, error: null, data: null });

    try {
      const response = await fetchAdmin(config.buildEndpoint(identifier.trim()));
      setRequestState({ loading: false, error: null, data: response });
      setRecoveryState({ loading: false, error: null, data: null });
    } catch (error) {
      setRequestState({ loading: false, error: toErrorMessage(error), data: null });
    }
  };

  const handleRecoveryFieldChange = (field, value) => {
    setRecoveryForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleRecoveryConfirm = async () => {
    if (!blockchain?.id) {
      return;
    }

    const payload = {};

    if (recoveryForm.reason.trim()) {
      payload.reason = recoveryForm.reason.trim();
    }

    if (recoveryForm.actor.trim()) {
      payload.actor = recoveryForm.actor.trim();
    }

    setRecoveryState({ loading: true, error: null, data: null });

    try {
      const response = await postAdmin(`/transactions/blockchain/${blockchain.id}/recover`, payload);
      setRecoveryState({ loading: false, error: null, data: response });
      setRecoveryDialogOpen(false);
    } catch (error) {
      setRecoveryState({ loading: false, error: toErrorMessage(error), data: null });
    }
  };

  const payloadButton = (title, value) => (
    <Button
      size="small"
      variant="text"
      onClick={() => setJsonDialog({ open: true, title, value })}
    >
      View payload
    </Button>
  );

  return (
    <AdminRouteLayout title="Blockchain Ops">
      <Stack spacing={3}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography variant="h5">Blockchain Ops</Typography>
                <Typography variant="body2" color="text.secondary">
                  Contract-backed crypto recovery timelines and deposit recovery controls.
                </Typography>
              </Stack>
              <Tabs value={lookupMode} variant="scrollable" allowScrollButtonsMobile>
                <Tab
                  value="blockchain"
                  label="By Blockchain Tx"
                  component={RouterLink}
                  to={PATH_DASHBOARD.blockchainOps.blockchainTx}
                />
                <Tab value="quote" label="By Quote" component={RouterLink} to={PATH_DASHBOARD.blockchainOps.quote} />
                <Tab
                  value="transaction"
                  label="By Lens Transaction"
                  component={RouterLink}
                  to={PATH_DASHBOARD.blockchainOps.transaction}
                />
              </Tabs>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle1">{config.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {config.description}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label={config.fieldLabel}
                    placeholder={config.fieldPlaceholder}
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <LoadingButton
                    variant="contained"
                    fullWidth
                    size="large"
                    loading={requestState.loading}
                    onClick={handleLookup}
                  >
                    Load timeline
                  </LoadingButton>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <RequestState loading={requestState.loading} error={requestState.error}>
          {timelineData ? (
            <Stack spacing={3}>
              {summaryAlert ? (
                <Alert severity={summaryAlert.severity} variant="outlined">
                  <AlertTitle>{summaryAlert.title}</AlertTitle>
                  {summaryAlert.message}
                </Alert>
              ) : null}

              {recoveryState.error ? (
                <Alert severity="error" variant="outlined">
                  <AlertTitle>Recovery request failed</AlertTitle>
                  {recoveryState.error}
                </Alert>
              ) : null}

              {recoveryOutcome ? (
                <Alert severity={recoveryOutcome.severity} variant="outlined">
                  <AlertTitle>{recoveryOutcome.title}</AlertTitle>
                  {recoveryOutcome.message}
                </Alert>
              ) : null}

              {recoveryItems.length ? (
                <AdminDetailPanel title="Last Recovery Outcome" items={recoveryItems} />
              ) : null}

              {recoveryState.data?.data?.payload ? (
                <Button
                  variant="text"
                  sx={{ alignSelf: 'flex-start' }}
                  onClick={() =>
                    setJsonDialog({
                      open: true,
                      title: 'Recovery payload',
                      value: recoveryState.data.data.payload,
                    })
                  }
                >
                  View recovery payload
                </Button>
              ) : null}

              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  {blockchainItems.length ? (
                    <AdminDetailPanel title="Blockchain Anchor" items={blockchainItems} />
                  ) : (
                    <AdminNoDataState
                      title="No blockchain anchor returned"
                      description="The timeline response did not include a blockchain record."
                    />
                  )}
                </Grid>
                <Grid item xs={12} lg={6}>
                  {transactionItems.length ? (
                    <AdminDetailPanel title="Linked Lens Transaction" items={transactionItems} />
                  ) : (
                    <AdminNoDataState
                      title="No linked transaction"
                      description="The blockchain row exists without a linked lens transaction."
                    />
                  )}
                </Grid>
              </Grid>

              {blockchain ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {blockchain.status ? <AdminStatusChip status={blockchain.status} /> : null}
                  {blockchain.swap_status ? <AdminStatusChip status={blockchain.swap_status} /> : null}
                  {blockchain.recovery_status ? <AdminStatusChip status={blockchain.recovery_status} /> : null}
                  {blockchain.conversion_dispatch_status ? (
                    <AdminStatusChip status={blockchain.conversion_dispatch_status} />
                  ) : null}
                </Stack>
              ) : null}

              {recoveryActionable ? (
                <AdminActionBar
                  title="Recovery Action"
                  description="The current blockchain recovery or dispatch state is documented as actionable."
                  actions={[
                    {
                      label: 'Open Recovery Dialog',
                      color: 'warning',
                      variant: 'contained',
                      icon: 'solar:restart-bold',
                    },
                  ]}
                  onActionClick={() => setRecoveryDialogOpen(true)}
                />
              ) : null}

              <AdminDataTable
                title="Swaps"
                description="Chronological swap records linked to the blockchain timeline."
                columns={[
                  { field: 'quote_id', headerName: 'Quote ID' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'is_finalized', headerName: 'Finalized' },
                  { field: 'from_currency', headerName: 'From' },
                  { field: 'to_currency', headerName: 'To' },
                  { field: 'from_amount', headerName: 'From Amount' },
                  { field: 'to_amount', headerName: 'To Amount' },
                  { field: 'settlement_amount', headerName: 'Settlement' },
                  { field: 'created_at', headerName: 'Created At' },
                ]}
                rows={buildTableRows(swaps).map((row) => ({
                  ...row,
                  is_finalized: Number(row.is_finalized) === 1 ? 'Yes' : 'No',
                }))}
                emptyTitle="No swap rows"
                emptyDescription="No swaps were returned for this timeline."
              />

              <AdminDataTable
                title="Ledger Entries"
                description="Chronological ledger entry records for the linked transaction."
                columns={[
                  { field: 'wallet_type', headerName: 'Wallet Type' },
                  { field: 'entry_type', headerName: 'Entry Type' },
                  { field: 'amount', headerName: 'Amount' },
                  { field: 'balance_before', headerName: 'Balance Before' },
                  { field: 'balance_after', headerName: 'Balance After' },
                  {
                    field: 'meta',
                    headerName: 'Meta',
                    render: (row) => (row.meta ? payloadButton('Ledger entry meta', row.meta) : '—'),
                  },
                  { field: 'created_at', headerName: 'Created At' },
                ]}
                rows={buildTableRows(timelineData.ledger_entries || [])}
                emptyTitle="No ledger entries"
                emptyDescription="No ledger entries were returned for this timeline."
              />

              <AdminDataTable
                title="Sweeps"
                description="Sweep records in chronological order."
                columns={[
                  { field: 'reference', headerName: 'Reference' },
                  { field: 'amount', headerName: 'Amount' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'trigger_source', headerName: 'Trigger Source' },
                  { field: 'sweep_reason', headerName: 'Sweep Reason' },
                  { field: 'status_detail', headerName: 'Status Detail' },
                  {
                    field: 'provider_response',
                    headerName: 'Provider Response',
                    render: (row) =>
                      row.provider_response ? payloadButton('Sweep provider response', row.provider_response) : '—',
                  },
                  { field: 'created_at', headerName: 'Created At' },
                ]}
                rows={buildTableRows(timelineData.sweeps || [])}
                emptyTitle="No sweeps"
                emptyDescription="No sweep records were returned for this timeline."
              />

              <AdminDataTable
                title="Bank Transfers"
                description="Bank transfer records linked to the recovery timeline."
                columns={[
                  { field: 'reference', headerName: 'Reference' },
                  { field: 'amount', headerName: 'Amount' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'provider', headerName: 'Provider' },
                  { field: 'created_at', headerName: 'Created At' },
                  { field: 'updated_at', headerName: 'Updated At' },
                ]}
                rows={buildTableRows(timelineData.bank_transfers || [])}
                emptyTitle="No bank transfers"
                emptyDescription="No bank transfers were returned for this timeline."
              />

              <AdminDataTable
                title="Cases"
                description="Review cases surfaced in chronological order."
                columns={[
                  { field: 'case_id', headerName: 'Case ID' },
                  { field: 'case_type', headerName: 'Case Type' },
                  { field: 'status', headerName: 'Status' },
                  { field: 'priority', headerName: 'Priority' },
                  { field: 'summary', headerName: 'Summary' },
                  { field: 'created_at', headerName: 'Created At' },
                ]}
                rows={buildTableRows(timelineData.cases || [])}
                emptyTitle="No cases"
                emptyDescription="No review cases were returned for this timeline."
              />

              {timelineData.domain_events?.length ? (
                <AdminTimeline title="Domain Events" items={buildEventTimeline(timelineData.domain_events)} />
              ) : (
                <AdminNoDataState
                  title="No domain events"
                  description="The response did not include any domain events for this timeline."
                />
              )}
            </Stack>
          ) : (
            <AdminNoDataState
              title="No blockchain timeline loaded"
              description="Select a documented lookup mode, enter an identifier, and load the timeline."
              icon="solar:database-bold"
            />
          )}
        </RequestState>
      </Stack>

      <RecoveryDialog
        open={recoveryDialogOpen}
        form={recoveryForm}
        loading={recoveryState.loading}
        onClose={() => setRecoveryDialogOpen(false)}
        onChange={handleRecoveryFieldChange}
        onConfirm={handleRecoveryConfirm}
      />

      <JsonPayloadDialog
        open={jsonDialog.open}
        title={jsonDialog.title}
        value={jsonDialog.value}
        onClose={() => setJsonDialog({ open: false, title: '', value: null })}
      />
    </AdminRouteLayout>
  );
}

BlockchainOpsPage.propTypes = {
  lookupMode: PropTypes.oneOf(['blockchain', 'quote', 'transaction']).isRequired,
};
